/**
 * Echo Hunt -- the retention check prompt (P7, §6).
 *
 * VISIBILITY-MATRIX ROW (§16.0): "Retention check".
 *  - knows real/fake?      real-only step (it compares a source event against its reshaped version, so
 *                          it only ever runs on real bullets).
 *  - sees world/character?  NO (invariant #4).
 *  - sees other bullets?    NO -- exactly one source/reshaped pair per call.
 *  - sees corpus?           NO.
 *
 * ## Why this exists (§17 "real bullets eroded by reshape/weave/rewrite")
 *
 * The shared reshape (§6) asks a model to add seeded words to a real recollection. A model that
 * over-reaches can quietly replace the event with a different one -- and the resulting bullet is then
 * labelled REAL while describing something the user never dreamt. That is worse than losing the bullet:
 * it trains a false memory (§17) and makes the familiarity signal meaningless.
 *
 * So every reshaped REAL bullet is checked back against its source, and a failed check regenerates the
 * reshape (up to 2 more times, then the bullet is dropped, §16.2 P7).
 *
 * Fakes need no such check: there is no source event to drift from.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §6, §17, §16.2 P7.
 */
import {JSONSchemaLike} from "../LLM.js";

/** What counts as "the same event". Stated in the prompt so the answer is not a matter of taste. */
export const RETENTION_RULES = [
	"`sameEvent` is true when the rewrite still describes the SAME thing happening: the same core action, the same outcome, and the same participants.",
	"`sameEvent` is still true when extra incidental details were added -- that is expected, and it is not a reason to fail.",
	"`sameEvent` is still true when the wording and sentence structure are completely different. The rewrite is SUPPOSED to be told in new words; judge only what happens, never how it is phrased.",
	"`sameEvent` is false when the central action, the outcome, or who was involved changed.",
	"`sameEvent` is false when the rewrite became about a different situation entirely.",
	"`sameEvent` is false when the original event can no longer be recognised in the rewrite.",
].map(a=>`- ${a}`).join("\n");

export function Retention_SystemPrompt(): string {
	return [
		"You compare two short descriptions of an event and decide whether they describe the same event.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

export type Retention_UserPromptOptions = {
	/** the bullet as it came out of the index pass (§4.2) -- the definition of the real event */
	sourceText: string;
	/** the same bullet after the shared reshape (§6) */
	reshapedText: string;
};

export function Retention_UserPrompt(opts: Retention_UserPromptOptions): string {
	return [
		"## Task",
		"Decide whether text B still describes the same event as text A.",
		"",
		"## Text A -- the original event",
		opts.sourceText.trim(),
		"",
		"## Text B -- the rewritten version",
		opts.reshapedText.trim(),
		"",
		"## Rules",
		RETENTION_RULES,
		"",
		"## Output",
		"Reply with ONE JSON object: {\"sameEvent\": true, \"reason\": \"...\"}",
		"`reason` is one short sentence naming what changed (or that nothing did).",
	].join("\n");
}

/** The JSON shape `CallLLM` validates each attempt against. */
export function Retention_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["sameEvent"],
		properties: {
			sameEvent: {type: "boolean"},
			reason: {type: "string"},
		},
	};
}
